import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../../services/firebase";

const AuthContext = createContext();

const buildUserFromTokenResult = (firebaseUser, tokenResult) => {
  const claims = tokenResult?.claims || {};
  const role =
    typeof claims.role === "string" && claims.role.trim()
      ? claims.role
      : "user";
  const storyEditor = claims.storyEditor === true;
  const isAdmin = role === "admin";

  return {
    firebaseUser,
    claims,
    role,
    storyEditor,
    isAdmin,
    canEditStories: isAdmin || storyEditor,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const tokenResult = await currentUser.getIdTokenResult(true);
          setUser(buildUserFromTokenResult(currentUser, tokenResult));
        } catch (error) {
          console.error("Error getting ID token result:", error);
          setUser(buildUserFromTokenResult(currentUser));
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signup = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setAuthLoading(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const loginWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const totalLoading = authLoading;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: authLoading,
        authLoading,
        totalLoading,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        loginWithGoogle,
      }}
    >
      {!totalLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
